import type { Maybe } from '@zaptic-external/ts-monads'
import classNames from 'classnames'
import i18next from 'i18next'
import * as React from 'react'
import type { FileExtension } from '../../../shared/domain/files/fileExtensions.no-deps'
import type { FileId, FileMetaApp } from '../../../shared/domain/files/types.no-deps'
import {
    type CreateAnswerBase,
    isDefaultGeneratedAnswer,
    getFileIdsFromAnswers,
    findAnswer,
    getAnswerWithSubtaskReport,
    areAnswersDifferent,
} from '../../../shared/domain/flows/answer.no-deps'
import {
    findElement,
    getAllElements,
    getGuideElementIds,
    getGuideElementTagIdsFromSection,
    getLoopPathDepthForElement,
} from '../../../shared/domain/flows/element.no-deps'
import { getReportTitle } from '../../../shared/domain/flows/reportTitle.no-deps'
import { type FlowUnitIds, getUnitsIdsToFetch } from '../../../shared/domain/flows/FlowUnitsIds.no-deps'
import type { KnownUnit } from '../unit/unit'
import type { AppUnitType, UnitData, UnitVersion } from '../../../shared/domain/units/types.no-deps'
import { ReportSubject } from '../ui/ReportSubject'
import { mapMerge, mapSet } from '../../../shared/lib/functional/map.no-deps'
import { createPromiseManager, cancelPromiseManagers, renderPromiseManagers } from '../util/PromiseManager'
import { createStateLens } from '../util/react'
import { findSection, findSectionWithElement } from '../../../shared/domain/flows/section.no-deps'
import {
    getSectionFailures,
    getSectionSummary,
    hasElementInSummary,
    hasStepInSummary,
} from '../../../shared/domain/flows/summary.no-deps'
import { hasLabel, isGuideElement } from '../../../shared/domain/flows/typeGuards.no-deps'
import type * as Flows from '../../../shared/domain/flows/types.no-deps'
import type { ClientLogger } from '../../../shared/lib/ClientLogger.no-deps'
import { assumeExhaustion, type UUID, type Nullable } from '../../../shared/lib/typeHelpers.no-deps'
import { getPathPrefix } from '../../../shared/webcomponents/helpers/href'
import type { CommonDeps } from '../base/CommonDeps'
import { renderCatch } from '../error/renderCatch'
import {
    type CreateSubtaskArgs,
    ExecutionPageBody,
    type ElementToScrollTo,
    type Workflow,
} from '../flow/ExecutionPageBody'
import { ExecutionPageTopBar } from '../flow/ExecutionPageTopBar'
import type { RenderCachedImage, WithCachedImageUrl } from '../image/WithCachedImage'
import { Page } from '../page/Page'
import type { DeviceIndexLookup } from '../photo/DeviceIndexLookup'
import { SVG_Info } from '../svg/SVG_Info'
import type { BarcodeResult } from '../unit/barcodeTypes'
import type { FileUploadData } from '../upload/FileUploadData'
import { hasUnsyncedData, updateUploads, type Upload, type UploadGroupId } from '../upload/Upload'
import type { Profile } from '../user/Profile'
import { initialPromiseState, type PromiseState, promiseStateMaybeGet, promiseStateUpdate } from '../util/PromiseState'
import { elementDomId, parseElementDomId, shouldScroll } from './element'
import { ExecutionSummaryPage } from './ExecutionSummaryPage'
import type { FlowReportStatus, FlowReportUpdate } from './FlowReport'
import { OpenStateModal } from './OpenStateModal'
import { readyToStart } from './SubjectType'
import type { SubtaskReport } from './ElementSubtask'
import type { ReportClientId } from '../../../shared/domain/reports/types.no-deps'
import { FileUrlManager, type FileUrlStates } from '../file/FileUrlManager'
import { timeout } from '../../../shared/lib/timeout.no-deps'
import { createPromiseThunkQueue } from '../../../shared/lib/PromiseThunkQueue.no-deps'
import { doNothing } from '../../../shared/lib/functional/function.no-deps'
import type { WorkflowReference } from '../../../shared/domain/workflows/types.no-deps'
import {
    fromLoopPath,
    type LoopPath,
    type LoopPathIds,
    toLoopPath,
} from '../../../shared/domain/flows/LoopPath.no-deps'
import { getAllUnitIdsInLoopPlan, type LoopPlan } from '../../../shared/domain/flows/LoopPlan.no-deps'
import { pipe } from '../../../shared/lib/functional/pipe.no-deps'
import { uniq } from '../../../shared/lib/functional/array.no-deps'
import type * as Guide from '../../../shared/domain/guides/types.no-deps'
import type { CachedUnitData } from '../../../shared/domain/units/CachedUnitData.no-deps'
import type { FeatureFlag } from '../../../shared/domain/status/types.no-deps'
import type { AppFileGuideDetails, AppGuide, ParentInfo, WorkflowAppGuide } from '../guide/Model'
import { PopupMessageModal } from '../../../shared/webcomponents/react-wrappers/PopupMessageModal'
import { MarkGuideAsCompleteFooter } from '../guide/MarkGuideAsCompleteFooter'
import type { AiChatUserContext } from '../../../shared/domain/aiChats/types.no-deps'
import type {
    HistoricalReport,
    ViewablePageReport,
    ViewableReportData,
} from '../../../shared/domain/viewableReports/types.no-deps'
import { getStepGuideIds } from '../../../shared/domain/flows/step.no-deps'
import { wait } from '../../../shared/lib/helpers.no-deps'
import { ElementOptions, type ElementSheet, type QuotedElement } from './ElementOptions/ElementOptions'
import { ExecutionPageSubmitButton } from './ExecutionPageSubmitButton'
import { IconButton } from '../ui/IconButton'
import { BaseBottomSheet } from '../ui/BaseBottomSheet'
import { type BottomSheetOptionProps, BottomSheetOptions } from '../ui/BottomSheetOptions'
import { tuple } from '../../../shared/lib/functional/tuple.no-deps'
import { ExpertsAndCollaboratorsList } from '../ui/ExpertsAndCollaboratorsList'
import {
    EmailSection,
    MsTeamsSection,
    PhoneNumberSection,
    UrlSection,
    WhatsAppSection,
} from '../ui/UserProfile/ContactSections'
import type { CamelCaseFlags } from '../../../shared/cfg/flags.no-deps'
import { keyBy } from 'lodash'
import { ZapticReportTile } from '../ui/ZapticReportTile'
import type { UnitWorkflowParameter } from '../../../shared/domain/UnitWorkflowParameter/types.no-deps'
import { isNil, isNotNil } from '../../../shared/lib/functional/nil.no-deps'

export type ReportType = 'action' | 'task' | 'training' | 'guide'
export interface Report {
    id: Flows.ReportId
    openState: FlowReportStatus
    type: ReportType
    flow: Flows.Flow
    flowTitle: string
    startDate: Date
    dueDate?: Date
    assignee?: { teams: string[] }
    completionDate?: Date | null
    collaborators?: {
        invited: number[]
        accepted: number[]
    }
    flowUnitIds: FlowUnitIds
    loopPlan: LoopPlan
    scheduleParameters: UnitWorkflowParameter[]
    cachedUnitData: CachedUnitData
    subjectUnitId?: number
    subjectUnitData: UnitData
    subjectUnitVersion: UUID
    parentClientId?: Flows.ReportId
    parentSectionId?: Flows.ElementId
    progenitorClientId?: Flows.ReportId
    progenitorOpenState?: FlowReportStatus
    parentReportTitle?: string
    parentSubjectUnitId?: number
    subtaskReports: Map<Flows.ReportId, SubtaskReport>
    procedure: {
        id: number
        name: string
    }
    workflowName?: string
    workflowRequiresSignature?: boolean
    originId?: number
    scheduledInstanceId?: UUID
}

export interface Props {
    deps: CommonDeps
    reportId: Flows.ReportId
    sectionId: Flows.ElementId
    reportPostId?: UUID
    loopPathIds: LoopPathIds
    startTimestamp?: Date
    createAnswerBase: CreateAnswerBase
    fetchReport: (reportId: Flows.ReportId) => Promise<Report>
    fetchReportHistory: (report: Report) => Promise<HistoricalReport[]>
    fetchAnswers: (reportId: Flows.ReportId) => Promise<Flows.Answer[]>
    fetchFilesMetaData: () => Promise<Map<FileId, FileMetaApp>>
    fetchUnit: (logger: ClientLogger, unitId: number) => Promise<KnownUnit>
    fetchAllUnits: (unitIds: number[]) => Promise<KnownUnit[]>
    fetchUserProfiles: () => Promise<Profile[]>
    fetchSubtask?: (logger: ClientLogger, reportClientId: ReportClientId) => Promise<SubtaskReport>
    fetchRequiredGuidesMap: (
        logger: ClientLogger,
        flow: Flows.Flow
    ) => Promise<Map<Guide.Id, Pick<Guide.SyncGuide, 'isDeleted'>>>
    fetchGuideElementsContextMap: (
        logger: ClientLogger,
        flow: Flows.Flow
    ) => Promise<Map<Guide.Id, { guide: Guide.SyncGuide; workflow: WorkflowReference }>>
    fetchOriginatingReportsCount: (scheduledInstanceId: UUID) => Promise<number>
    fetchOriginViewReportData?: (scheduledInstanceId: UUID) => Promise<ViewablePageReport>
    writeAnswer: (answer: Flows.Answer) => Promise<void>
    removeAnswers?: (answer: Flows.Answer[]) => Promise<void>
    reportUpdate?: {
        subscribe: (fn: (update: FlowReportUpdate) => void) => void
        unsubscribe: (fn: (update: FlowReportUpdate) => void) => void
    }
    routeBack: () => void
    routeToPostPage: (postId: UUID) => void
    routeToReportPage: (reportId: number) => void
    routeToExecutionPage: (
        reportId: Flows.ReportId,
        opts?: {
            sectionId?: Flows.ElementId
            comingFromActionScheduledInstance?: boolean
            comingFromScheduledInstance?: UUID
            comingFromSubTask?: Flows.ReportId
            comingFromUnit?: number
            comingFromWorkflow?: number
            loopPathIds?: LoopPathIds
            scrollToElementId?: Flows.ElementId
            scrollToLoopPath?: LoopPath
            scrollTop?: number
        }
    ) => void
    routeToCompletedPage: () => Promise<void>
    routeToGuideExecutionPage: (
        guideId: Guide.Id,
        opts: { guideElementId: Flows.ElementId; parentClientId: Flows.ReportId; guideId: Guide.Id }
    ) => void
    guideElementId?: Flows.ElementId
    guideId?: Guide.Id
    fetchWorkflowVersion?: (workflowId: number) => Promise<number>
    onSubmit: (flowSummary: Flows.FlowSummary) => Promise<void>
    onCancel: () => void
    cordovaGetMedia?: (type: 'camera' | 'gallery', cameraType: Flows.CameraType) => Promise<string>
    headerExtra?: React.ReactElement<unknown>
    footer?: React.ReactElement<unknown>
    fetchUploads: (groupdId: UploadGroupId) => Promise<Upload[]>
    subscribeToUploads: (groupdId: UploadGroupId, onChange: (uploads: Upload[]) => void) => void
    unsubscribeToUploads: (groupdId: UploadGroupId, onChange: (uploads: Upload[]) => void) => void
    selectFile: (
        fileExtensions: FileExtension[],
        isIOS?: boolean,
        type?: 'image' | 'video'
    ) => Promise<FileUploadData[]>
    saveBlob: (blob: Blob) => Promise<FileUploadData>
    saveFile?: (fileId: FileId, path: string) => Promise<void>
    readBlob: (fileId: FileId) => Promise<Blob>
    cancelUpload: (fileId: FileId, logger: ClientLogger) => Promise<void>
    showFileProgress: boolean
    renderCachedImage: RenderCachedImage
    onClickGuideParentInfo?: (parentInfo: ParentInfo) => void
    withCachedImageUrl: WithCachedImageUrl
    routeForReportSubject?: (unitId: number) => void
    hasTouchKeyboard?: boolean
    initialScrollTop?: number
    featureFlags: Pick<
        CamelCaseFlags,
        | 'reportsTranslation'
        | 'reportCommentsTranslation'
        | 'workflowFeedbackV2'
        | 'appendToReportTitle'
        | 'useNewElementNumberInput'
    >
    fetchUnitsByIds: (unitIds: number[]) => Promise<KnownUnit[]>
    fetchUnitsByType: (unitTypes: number[]) => Promise<KnownUnit[]>
    fetchUnitTypeName: (unitTypeId: number) => Promise<Maybe<string>>
    fetchUnitType: (unitTypeId: number) => Promise<AppUnitType | null>
    saveUnitSubject: (reportId: Flows.ReportId, flow: Flows.Flow, unit: KnownUnit) => Promise<void>
    routeToProcedurePage: (procedure: number) => void
    fetchWorkflows: (flow: Flows.Flow) => Promise<Workflow[]>
    createSubtask: (args: CreateSubtaskArgs) => Promise<Flows.ReportId>
    deleteSubtask: (reportId: Flows.ReportId) => Promise<void>
    comingFromActionScheduledInstance?: boolean
    comingFromScheduledInstance?: UUID
    comingFromSubTask?: Flows.ReportId
    comingFromUnit?: number
    comingFromWorkflow?: number
    parentSectionId?: Flows.ElementId
    parentScrollTop?: number
    markReportAsCompleted: (
        logger: ClientLogger,
        reportId: Flows.ReportId,
        flowSummary: Flows.FlowSummary
    ) => Promise<void>
    markReportAsIncomplete: (logger: ClientLogger, reportId: Flows.ReportId) => Promise<void>
    savePathForRecovery?: (elementToRecover: Flows.ElementId, scrollTop: number) => Promise<void>
    recovery?: { elementId: Flows.ElementId; value: unknown; clear: () => void }
    saveTitle?: (reportId: Flows.ReportId, title: string) => Promise<void>
    barcodeScanForUnits?: {
        scan: (units: KnownUnit[]) => Promise<BarcodeResult>
        fetchUnitType: (id: number) => Promise<AppUnitType>
    }
    cachedSubjectUnit?: UnitVersion | null
    isIOS?: boolean
    isAndroid?: boolean
    browserCameraDeviceIndexLookup: DeviceIndexLookup
    forceUseBrowserCameraAPI?: Promise<boolean>
    dispatchReportInvalid?: () => void
    featureEnabled: (featureFlag: FeatureFlag) => boolean
    fetchFileGuideDetailsMap: (fileIds: FileId[]) => Promise<Map<FileId, AppFileGuideDetails>>
    fetchGuideParentInfo: (syncGuide: Guide.SyncGuide) => Promise<ParentInfo[]>
    fetchReportGuideDetails: (reportGuideId: Guide.ReportGuideId) => Promise<Guide.ReportGuideDetails>
    fetchAppGuides: (syncGuides: Guide.SyncGuide[], withParentInfo: boolean) => Promise<AppGuide[]>
    fetchSyncGuides: (guideIds: Guide.Id[], tagIds?: number[]) => Promise<Guide.SyncGuide[]>
    fetchGuideReportId: (guide: WorkflowAppGuide, parentClientId?: Flows.ReportId) => Promise<Flows.ReportId>
    onOpenFile: (downloadUrl: string, fileExtension: FileExtension, name: string) => Promise<void>
    onLinkedGuidesToStep: (syncGuides: Guide.SyncGuide[]) => void
    onChangeZapBotContext: (context: Nullable<AiChatUserContext>) => void
    showHeader: boolean
    allowImageZoom: boolean
    sign?: (reportClientId: ReportClientId) => void
    alwaysShowSubmitButton?: boolean
    scrollToElementId?: Flows.ElementId
    scrollToLoopPath?: LoopPath
    submitFeedback?: (elementId: Flows.ElementId, message: string, loopPath?: LoopPath) => void
    hasAccessToProcedure: (procedureId: number) => Promise<boolean>
    previewMode?: boolean
    comment?: UUID
}

interface State {
    flowReportState: PromiseState<Report>
    flowAnswersState: PromiseState<Flows.Answer[]>
    filesMetaDataState: PromiseState<Map<FileId, FileMetaApp>>
    allUnitsState: PromiseState<KnownUnit[]>
    userProfilesState: PromiseState<Profile[]>
    submitState: PromiseState<void>
    showValidation: boolean
    hideHeader?: boolean
    hideFooter: boolean
    reportSubjectState: PromiseState<KnownUnit>
    workflowsState: PromiseState<Workflow[]>
    uploadsState: PromiseState<Upload[]>
    elementToScrollTo?: ElementToScrollTo | null
    showGuidesBottomSheet: boolean
    requiredGuidesMapState: PromiseState<Map<Guide.Id, Pick<Guide.SyncGuide, 'isDeleted'>>>
    guideElementsContextMapState: PromiseState<Map<Guide.Id, { guide: Guide.SyncGuide; workflow: WorkflowReference }>>
    isReauthModalOpen: boolean
    sectionSummary: Flows.FlowSummary
    viewReportDataState: PromiseState<ViewableReportData>
    elementSheet?: ElementSheet
    // Modal to show options for the execution page i.e cancel or submit
    isOptionsModalOpen: boolean
    // Modal to show experts list
    expertsModalOpen: boolean
    // Modal to show contact options for an expert e.g phone, whatsapp, msteams, email and url
    // It will either be null or have the expert id
    expertsModalSelectedExpert: number | null
}

/**
 * Note to all consumers. On Android, when using capturing photos or videos the OS may kill the main process
 * and we need to be able to recover state using the url alone.
 */

export class ExecutionPage extends React.Component<Props, State> {
    public state: State = {
        flowReportState: initialPromiseState,
        flowAnswersState: initialPromiseState,
        filesMetaDataState: initialPromiseState,
        allUnitsState: initialPromiseState,
        userProfilesState: initialPromiseState,
        submitState: initialPromiseState,
        showValidation: false,
        hideFooter: false,
        reportSubjectState: initialPromiseState,
        workflowsState: initialPromiseState,
        uploadsState: initialPromiseState,
        showGuidesBottomSheet: false,
        requiredGuidesMapState: initialPromiseState,
        guideElementsContextMapState: initialPromiseState,
        isReauthModalOpen: false,
        sectionSummary: null,
        viewReportDataState: initialPromiseState,
        elementSheet: null,
        isOptionsModalOpen: false,
        expertsModalOpen: false,
        expertsModalSelectedExpert: null,
    }

    private pageElement: HTMLElement
    private startTimestamp: Date

    /**
     * More info - https://code.zaptic.com/zaptic/zaptic/blob/develop/app/docs/RenderingPromises.md
     *
     */
    private promiseManagers = {
        flowReport: createPromiseManager(() => this.props.deps, createStateLens(this, 'flowReportState')),
        flowAnswers: createPromiseManager(() => this.props.deps, createStateLens(this, 'flowAnswersState')),
        filesMetaData: createPromiseManager(() => this.props.deps, createStateLens(this, 'filesMetaDataState')),
        allUnits: createPromiseManager(() => this.props.deps, createStateLens(this, 'allUnitsState')),
        userProfiles: createPromiseManager(() => this.props.deps, createStateLens(this, 'userProfilesState')),
        reportSubject: createPromiseManager(() => this.props.deps, createStateLens(this, 'reportSubjectState')),
        submit: createPromiseManager(() => this.props.deps, createStateLens(this, 'submitState')),
        workflows: createPromiseManager(() => this.props.deps, createStateLens(this, 'workflowsState')),
        uploads: createPromiseManager(() => this.props.deps, createStateLens(this, 'uploadsState')),
        requiredGuidesMap: createPromiseManager(() => this.props.deps, createStateLens(this, 'requiredGuidesMapState')),
        guideElementsContextMap: createPromiseManager(
            () => this.props.deps,
            createStateLens(this, 'guideElementsContextMapState')
        ),
        viewReportData: createPromiseManager(() => this.props.deps, createStateLens(this, 'viewReportDataState')),
    }

    private promiseThunkQueue = createPromiseThunkQueue(this.props.deps.createLogger(`ExecutionPage`), 30000)

    public async componentDidMount() {
        const logger = this.props.deps.createLogger(`ExecutionPage.componentDidMount`)

        if (isNotNil(this.props.comment)) {
            this.openComments(null, this.props.comment)
        }

        this.pageElement.addEventListener('focusout', this.onFocusChange)
        this.pageElement.addEventListener('focusin', this.onFocusChange)
        this.props.reportUpdate?.subscribe(this.onReportUpdate)

        // Fetch answers
        const answersP = this.promiseManagers.flowAnswers.trigger(() => this.props.fetchAnswers(this.props.reportId))

        // Fetch files meta data
        const filesMetaDataP = this.promiseManagers.filesMetaData.trigger(this.props.fetchFilesMetaData)

        // Fetch uploads
        const uploadsP = this.promiseManagers.uploads.trigger(() => this.props.fetchUploads(this.props.reportId))
        this.props.subscribeToUploads(this.props.reportId, this.updateUploads)

        // Fetch the report
        const report = await this.promiseManagers.flowReport.trigger(() => this.props.fetchReport(this.props.reportId))

        logger.log(`report loaded`, { reportClientId: report.id })

        // Fetch subject unit
        const subjectUnitP = this.promiseManagers.reportSubject.trigger(async () => {
            if (report.subjectUnitId == null) {
                return
            }
            const unit = await this.props.fetchUnit(logger, report.subjectUnitId)
            if (this.props.cachedSubjectUnit == null) {
                return unit
            }
            return { ...unit, data: this.props.cachedSubjectUnit.data }
        })

        const workflowsP = this.promiseManagers.workflows.trigger(() => this.props.fetchWorkflows(report.flow))

        // Fetch user profiles
        const usersP = this.promiseManagers.userProfiles.trigger(() => this.props.fetchUserProfiles())

        // Fetch required guides map for the flow
        const requiredGuidesMapP = this.promiseManagers.requiredGuidesMap.trigger(() =>
            this.props.fetchRequiredGuidesMap(logger, report.flow)
        )

        const guideElementsContextMapP = this.promiseManagers.guideElementsContextMap.trigger(() =>
            this.props.fetchGuideElementsContextMap(logger, report.flow)
        )

        // Wait for everything to have loaded
        const [answers] = await Promise.all([
            answersP,
            workflowsP,
            usersP,
            subjectUnitP,
            uploadsP,
            filesMetaDataP,
            requiredGuidesMapP,
            guideElementsContextMapP,
        ])

        const unitIdsPromise = new Promise<number[]>((resolve) => {
            const unitIds = pipe(
                [...getUnitsIdsToFetch(report.flowUnitIds), ...getAllUnitIdsInLoopPlan(report.loopPlan ?? [])],
                uniq
            )

            return resolve(unitIds)
        })

        unitIdsPromise.then((unitIds) =>
            // Fetch units after everything else because there can be so many of them that they
            // block them main thread to read them into memory.
            this.promiseManagers.allUnits.trigger(async () => {
                // Add timeout to allow for rending to flush and images to lazy load
                if (unitIds.length > 1000) await timeout(400)

                return this.props.fetchAllUnits(unitIds)
            })
        )

        logger.log(`data loaded`, { reportClientId: report.id })

        this.startTimestamp = this.props.startTimestamp ?? new Date()

        // If we're landing on this page from a sub-task, find the sub-task element and scroll to it
        // otherwise scroll to the top
        if (this.props.comingFromSubTask != null) {
            this.handleComingFromSubtask(report, answers, this.setInitialScrollPosition)
        } else if (this.props.scrollToElementId != null) {
            this.handleScrollToElement(report, answers, this.setInitialScrollPosition)
        }

        const { reportId } = this.props

        this.props.onChangeZapBotContext({
            reportClientId: reportId,
            guides: uniq([...getGuideElementIds(report.flow, false), ...getStepGuideIds(report.flow)]),
            procedures: [report.procedure.id],
            units: report.subjectUnitId ? [report.subjectUnitId] : [],
        })

        logger.log('success')
    }

    public componentWillUnmount() {
        cancelPromiseManagers(this.promiseManagers)
        if (this.pageElement) {
            this.pageElement.removeEventListener('focusout', this.onFocusChange)
            this.pageElement.removeEventListener('focusin', this.onFocusChange)
        }
        this.props.subscribeToUploads(this.props.reportId, this.updateUploads)
        this.props.reportUpdate?.unsubscribe(this.onReportUpdate)

        this.props.onChangeZapBotContext(null)
    }

    public async componentDidUpdate(prevProps: Props, prevState: State) {
        // If the section changes
        if (this.props.sectionId !== prevProps.sectionId) {
            if (this.props.comingFromSubTask) {
                this.scrollToSubtaskElement()
                return
            } else if (this.props.scrollToElementId) {
                if (this.props.scrollToLoopPath) {
                    this.scrollToElement(
                        this.props.scrollToElementId,
                        this.props.scrollToLoopPath,
                        this.setInitialScrollPosition
                    )
                } else {
                    const answers = this.promiseManagers.flowAnswers.mustGet()
                    const answer = findAnswer(answers, this.props.scrollToElementId, this.props.loopPathIds)
                    this.scrollToElement(answer.element, answer.loopPath, this.setInitialScrollPosition)
                }
                return
            }

            // If going back from a nested section, then the previous section will be an element on this page
            // So we can query the dom to see if we can scroll to the element
            // This means that the user doesn't have to find their place in the page again

            // Find the previous section as an element
            const previousSectionElement = document.getElementById(
                elementDomId(prevProps.sectionId, toLoopPath(prevProps.loopPathIds))
            )

            if (previousSectionElement != null) {
                // If the previous section is an element on the page then scroll to it
                previousSectionElement.scrollIntoView()

                // Wait for any images to load and try to scroll again
                timeout(200).then(() => previousSectionElement.scrollIntoView())
            } else {
                // Otherwise scroll to the top
                this.setScrollPosition(0)
            }
        }

        const report = this.promiseManagers.flowReport.maybeGet()
        const answers = this.promiseManagers.flowAnswers.maybeGet()
        const prevAnswers = promiseStateMaybeGet(prevState.flowAnswersState)

        // If there's a new answer in report
        const reportHasNewAnswers = report && prevAnswers && answers && areAnswersDifferent(prevAnswers, answers)

        // If there's any file select answer
        const hasFileSelectAnswer = reportHasNewAnswers && answers.some((answer) => answer.type === 'files-select')

        if (reportHasNewAnswers && hasFileSelectAnswer) {
            // Update files meta data map so that we see new file select answer in UI
            const newFilesMetaDataMap = await this.updateFilesMetaData()
            this.promiseManagers.filesMetaData.update((oldFilesMetaDataMap) =>
                mapMerge(oldFilesMetaDataMap, newFilesMetaDataMap)
            )
        }

        if (this.props.saveTitle && reportHasNewAnswers) {
            // We can pass an empty map here as guide element answer cannot be set as report title
            const requiredGuidesMap = new Map()
            const guideElementsContextMap = new Map()

            const previousTitle = getReportTitle(
                report.flow,
                this.getFlowSummary(report, prevAnswers, requiredGuidesMap, guideElementsContextMap, []),
                report.workflowName,
                this.props.featureFlags.appendToReportTitle
            )
            const title = getReportTitle(
                report.flow,
                this.getFlowSummary(report, answers, requiredGuidesMap, guideElementsContextMap, []),
                report.workflowName,
                this.props.featureFlags.appendToReportTitle
            )

            if (previousTitle !== title) {
                await this.props.saveTitle(report.id, title)
            }
        }
    }

    public render = () => renderCatch(this.props.deps, this.tryRender)

    private updateUploads = async (uploads: Upload[]) => {
        await this.promiseManagers.uploads.update((oldUploads) => updateUploads(oldUploads, uploads))
    }

    private handleComingFromSubtask(report: Report, answers: Flows.Answer[], onSubmit?: () => void) {
        const answer = getAnswerWithSubtaskReport(answers, this.props.comingFromSubTask)
        if (answer == null) return

        const section = findSectionWithElement(report.flow, answer.element)
        if (section == null) return

        if (section.id !== this.props.sectionId) {
            this.goToSection(report, section.id, {
                comingFromActionScheduledInstance: this.props.comingFromActionScheduledInstance,
                comingFromScheduledInstance: this.props.comingFromScheduledInstance,
                comingFromSubTask: this.props.comingFromSubTask,
                comingFromUnit: this.props.comingFromUnit,
                comingFromWorkflow: this.props.comingFromWorkflow,
                scrollTop: this.props.initialScrollTop,
            })
        } else {
            this.scrollToSubtaskElement(onSubmit)
        }
    }

    private handleScrollToElement(report: Report, answers: Flows.Answer[], onSubmit?: () => void) {
        const answer = findAnswer(answers, this.props.scrollToElementId, this.props.loopPathIds)
        if (answer == null) return

        const section = findSectionWithElement(report.flow, answer.element)
        if (section == null) return

        if (section.id !== this.props.sectionId) {
            this.goToSection(report, section.id, {
                comingFromActionScheduledInstance: this.props.comingFromActionScheduledInstance,
                comingFromScheduledInstance: this.props.comingFromScheduledInstance,
                comingFromUnit: this.props.comingFromUnit,
                comingFromWorkflow: this.props.comingFromWorkflow,
                scrollToElementId: this.props.scrollToElementId,
                scrollTop: this.props.initialScrollTop,
            })
        } else {
            this.scrollToElement(answer.element, answer.loopPath, onSubmit)
        }
    }

    private scrollToSubtaskElement = (onSubmit?: () => void) => {
        const answers = this.promiseManagers.flowAnswers.mustGet()
        const answer = getAnswerWithSubtaskReport(answers, this.props.comingFromSubTask)

        this.scrollToElement(answer.element, answer.loopPath, onSubmit)
    }

    private scrollToElement = (elementId: Flows.ElementId, loopPath: LoopPath, onSubmit?: () => void) => {
        const scrollableElementInfo: ElementToScrollTo = {
            elementId,
            loopPath,
            behavior: 'auto',
        }
        this.setState({ elementToScrollTo: scrollableElementInfo }, onSubmit)
    }

    private getSection(report: Report) {
        if (this.props.sectionId == null) return report.flow
        return findSection(report.flow, this.props.sectionId)
    }

    private getTitle() {
        // If the report's not loaded, don't show a title yet
        const report = this.promiseManagers.flowReport.maybeGet()
        if (report == null) return null

        // If we're in a subsection with a label, show section label as the title
        const section = this.getSection(report)
        if (section && hasLabel(section)) return section.label

        // If the report title has been set by an element, show that
        const dynamicTitle = this.getDynamicTitle(report)
        if (dynamicTitle != null) return dynamicTitle

        // Display report title if exist, otherwise fallback to workflow/activity name as the title
        return report.flowTitle ?? report.workflowName ?? ''
    }

    private getFlowSummary(
        report: Report,
        answers: Flows.Answer[],
        requiredGuidesMap: Map<Guide.Id, Pick<Guide.SyncGuide, 'isDeleted'>>,
        guideElementsContextMap: Map<Guide.Id, { guide: Guide.SyncGuide; workflow: WorkflowReference }>,
        allUnitsRelevantForFlow: KnownUnit[],
        loopPathIds: LoopPathIds = []
    ) {
        return this.getSectionSummary(
            report,
            report.flow,
            answers,
            requiredGuidesMap,
            guideElementsContextMap,
            loopPathIds,
            allUnitsRelevantForFlow
        )
    }

    private getCurrentSectionSummary(
        report: Report,
        answers: Flows.Answer[],
        requiredGuidesMap: Map<Guide.Id, Pick<Guide.SyncGuide, 'isDeleted'>>,
        guideElementsContextMap: Map<Guide.Id, { guide: Guide.SyncGuide; workflow: WorkflowReference }>,
        allUnitsRelevantForFlow: KnownUnit[]
    ) {
        const section = this.getSection(report)
        return this.getSectionSummary(
            report,
            section,
            answers,
            requiredGuidesMap,
            guideElementsContextMap,
            this.props.loopPathIds,
            allUnitsRelevantForFlow
        )
    }

    private isEditingReport(report: Report) {
        return report.completionDate != null
    }

    private hasReportSubject(report: Report) {
        return report.subjectUnitId !== null
    }

    private getSectionSummary(
        report: Report,
        section: Flows.Section,
        answers: Flows.Answer[],
        requiredGuidesMap: Map<Guide.Id, Pick<Guide.SyncGuide, 'isDeleted'>>,
        guideElementsContextMap: Map<Guide.Id, { guide: Guide.SyncGuide; workflow: WorkflowReference }>,
        loopPathIds: LoopPathIds,
        allUnitsRelevantForFlow: KnownUnit[]
    ) {
        const isSubtaskOpen = (reportId: Flows.ReportId) =>
            !this.isEditingReport(report) && report.subtaskReports.get(reportId)?.status === 'open'

        return getSectionSummary(
            section,
            answers,
            report.startDate,
            report.subjectUnitData,
            isSubtaskOpen,
            report.loopPlan,
            loopPathIds,
            allUnitsRelevantForFlow,
            report.cachedUnitData,
            requiredGuidesMap,
            guideElementsContextMap,
            report.completionDate
        )
    }

    private isCompletedReport(report: Report) {
        return Boolean(report.completionDate)
    }

    /**
     * This is the title that is set by a titleAssigningElement, inferred from answers
     */
    private getDynamicTitle(report: Report) {
        // If the answers haven't loaded yet, ignore
        const answers = this.promiseManagers.flowAnswers.maybeGet()

        if (answers == null) return null

        // We can pass an empty map here as guide element answer cannot be set as report title
        const requiredGuidesMap = new Map<Guide.Id, Pick<Guide.SyncGuide, 'isDeleted'>>()
        const guideElementsContextMap = new Map<Guide.Id, { guide: Guide.SyncGuide; workflow: WorkflowReference }>()
        // Otherwise infer a title from the answers
        const flowSummary = this.getFlowSummary(report, answers, requiredGuidesMap, guideElementsContextMap, [])

        return getReportTitle(
            report.flow,
            flowSummary,
            report.workflowName,
            this.props.featureFlags.appendToReportTitle
        )
    }

    private getPageBody() {
        return document.querySelector('.AppCore__page__Page__body')
    }

    private getScrollPosition() {
        return this.getPageBody()?.scrollTop ?? 0
    }

    private setScrollPosition(scrollTop: number) {
        return this.getPageBody()?.scrollTo(0, scrollTop)
    }

    private async setInitialScrollPosition() {
        if (this.props.initialScrollTop != null) {
            // Wait to let some images load
            await timeout(300)

            this.setScrollPosition(this.props.initialScrollTop)
        }
    }

    private updateFilesMetaData = async () => {
        // eslint-disable-next-line no-return-await
        return await this.props.fetchFilesMetaData()
    }

    private onReportUpdate = async (reportUpdate: FlowReportUpdate) => {
        switch (reportUpdate.type) {
            case 'OpenStateChangeUpdate':
                const report = this.promiseManagers.flowReport.maybeGet()

                // If this report's open state has changed
                if (reportUpdate.reportId === this.props.reportId) {
                    this.promiseManagers.flowReport.update((report) => ({
                        ...report,
                        openState: reportUpdate.openState,
                    }))
                }

                // If the progenitor's open state has changed
                if (reportUpdate.reportId === report?.progenitorClientId) {
                    this.promiseManagers.flowReport.update((report) => ({
                        ...report,
                        progenitorOpenState: reportUpdate.openState,
                    }))
                }

                break

            case 'NewAnswersUpdate':
                if (reportUpdate.reportId !== this.props.reportId) return
                this.promiseManagers.flowAnswers.update((anwsers) =>
                    anwsers
                        .filter((answer) => reportUpdate.newAnswers.every((newAnswer) => newAnswer.id !== answer.id))
                        .concat(reportUpdate.newAnswers)
                )
                break

            case 'ReportSynced':
                if (reportUpdate.parentClientId !== this.props.reportId) return

                // Only create logger after establish it's relevant to avoid noise
                const logger = this.props.deps.createLogger('onReportUpdate.ReportSynced')

                // Otherwise, fetch it
                const subtask = await this.props.fetchSubtask(logger, reportUpdate.reportId)

                // And put into the reports subtask map
                this.promiseManagers.flowReport.update((report) => ({
                    ...report,
                    subtaskReports: mapSet(report.subtaskReports, reportUpdate.reportId, subtask),
                }))
                break

            case 'UnitDataChange':
                if (reportUpdate.reportId !== this.props.reportId) return
                this.promiseManagers.flowReport.update((report) => ({
                    ...report,
                    cachedUnitData: reportUpdate.cachedUnitData,
                }))

                break

            default:
                assumeExhaustion(reportUpdate)
        }
    }

    private goToSection(
        report: Report,
        sectionId: Flows.ElementId,
        opts?: {
            comingFromActionScheduledInstance?: boolean
            comingFromScheduledInstance?: UUID
            comingFromSubTask?: Flows.ReportId
            comingFromUnit?: number
            comingFromWorkflow?: number
            scrollToElementId?: Flows.ElementId
            scrollToLoopPath?: LoopPath
            scrollTop?: number
        }
    ) {
        // Work out the loopPath of parent
        const desiredDepth = getLoopPathDepthForElement(report.flow, sectionId)
        const loopPathIds = this.props.loopPathIds.slice(0, desiredDepth)

        // Go back to the parent section
        this.props.routeToExecutionPage(report.id, {
            ...opts,
            sectionId,
            loopPathIds,
        })
    }

    private goToElement(report: Report, elementId: Flows.ElementId, loopPath: LoopPath) {
        const sectionId = findSectionWithElement(report.flow, elementId)?.id

        if (isNil(sectionId)) {
            return
        }

        if (sectionId !== this.props.sectionId) {
            this.goToSection(report, sectionId, {
                scrollToElementId: elementId,
                scrollToLoopPath: loopPath,
            })
        } else {
            this.scrollToElement(elementId, loopPath)
        }
    }

    private onBack = () => {
        const logger = this.props.deps.createLogger('ExecutionPage.back')

        // If the report's loaded
        const report = this.promiseManagers.flowReport.maybeGet()
        if (report != null) {
            // And we're in a subsection that has a parent
            const parentSection = findSectionWithElement(report.flow, this.props.sectionId)
            if (parentSection != null) {
                // Go back to the parent section
                this.goToSection(report, parentSection.id)

                logger.log(`routing to parent section`, {
                    reportClientId: report?.id,
                    parentSectionId: parentSection.id,
                })
                return
            }

            // Or we're in a report that has a parent
            const parentClientId = report.parentClientId
            if (parentClientId != null && report.type !== 'guide') {
                // Go back to the parent report
                this.routeToParentReport(report)

                logger.log(`routing to parent report`, { reportClientId: report?.id, parentClientId })
                return
            }

            if (this.props.guideElementId && parentClientId != null && report.type === 'guide') {
                this.props.routeToExecutionPage(report.parentClientId, {
                    scrollToElementId: this.props.guideElementId,
                    scrollTop: this.props.parentScrollTop,
                })

                return
            }
        }

        // Otherwise go back to the report summary/activity base
        this.props.routeBack()

        logger.log(`routing back`, { reportClientId: report?.id })
    }

    private onOpenStateModalDismiss = async () => {
        // If the report's loaded
        const report = this.promiseManagers.flowReport.maybeGet()

        // And we have a parent report
        if (report?.parentClientId !== null) {
            // Go back to that report
            this.routeToParentReport(report)
            return
        }

        // Otherwise use the provided callback to go to where we need to go
        this.props.routeToCompletedPage()
    }

    private onSubmit = () => {
        if (this.state.submitState.status === 'in-progress') return

        const report = this.promiseManagers.flowReport.maybeGet()
        const logger = this.props.deps.createLogger('ExecutionPage.onSubmit')

        this.promiseManagers.submit.trigger(async () => {
            try {
                // Wait for queue to flush
                await this.promiseThunkQueue.push(async () => doNothing())

                // Get summary for just this subsection to work out if it can be completed
                const answers = this.promiseManagers.flowAnswers.maybeGet()

                const allFlowUnits = this.promiseManagers.allUnits.maybeGet()
                const requiredGuidesMap = this.promiseManagers.requiredGuidesMap.maybeGet()
                const guideElementsContextMap = this.promiseManagers.guideElementsContextMap.maybeGet()

                const section = this.getSection(report)
                const sectionSummary = this.getSectionSummary(
                    report,
                    section,
                    answers,
                    requiredGuidesMap,
                    guideElementsContextMap,
                    this.props.loopPathIds,
                    allFlowUnits
                )
                this.setState({ sectionSummary })
                const failures = getSectionFailures(sectionSummary, section.id, this.props.loopPathIds)
                const invalid = failures.length > 0
                const reportClientId = report.id
                const sectionId = section?.id
                // If invalid, highlight invalid fields and scroll to first one
                if (invalid) {
                    logger.log(`failed validation`, { reportClientId, sectionId, failures })

                    if (!this.state.showValidation) {
                        await new Promise<void>((resolve) => this.setState({ showValidation: true }, resolve))
                    }

                    const scrollableElementInfo: ElementToScrollTo = {
                        elementId: failures[0].elementId,
                        loopPath: failures[0].loopPath,
                        behavior: 'smooth',
                    }
                    this.setState({ elementToScrollTo: scrollableElementInfo }, this.props.dispatchReportInvalid)

                    return
                }

                logger.log(`creating completed answer`, { reportClientId, sectionId })

                // Otherwise save that this section has been completed
                await this.createAnswer({
                    ...this.props.createAnswerBase(section.id, this.props.loopPathIds),
                    type: 'section',
                    value: 'completed',
                })

                logger.log(`created completed answer`, { reportClientId, sectionId })

                // If in a subsection that has a parent
                const parent = findSectionWithElement(report.flow, this.props.sectionId)
                if (parent != null) {
                    // Go back to the parent section
                    this.goToSection(report, parent.id)
                    logger.log(`subsection complete, navigating to parent section`, { reportClientId, sectionId })
                    return
                }

                if (report.parentClientId != null) {
                    // We've updated the answers to add the section answer, need to
                    // regen the section summary
                    const flowAnswers = this.promiseManagers.flowAnswers.mustGet()

                    const requiredGuidesMap = this.promiseManagers.requiredGuidesMap.mustGet()
                    const guideElementsContextMap = this.promiseManagers.guideElementsContextMap.mustGet()

                    const finalSectionSummary = this.getCurrentSectionSummary(
                        report,
                        flowAnswers,
                        requiredGuidesMap,
                        guideElementsContextMap,
                        allFlowUnits
                    )

                    await this.props.markReportAsCompleted(logger, report.id, finalSectionSummary)
                    logger.log(`marked subtask complete`, { reportClientId, sectionId })

                    this.routeToParentReport(report)
                    logger.log(`subtask complete, navigating to parent report`, { reportClientId, sectionId })
                    return
                }

                if (report.workflowRequiresSignature) {
                    if (this.props.sign) {
                        // When used from the app, signing logic is passed in
                        this.props.sign(reportClientId)
                    } else {
                        this.setState({ isReauthModalOpen: true })
                    }
                    return
                }

                // Otherwise finalize report
                await this.props.onSubmit(sectionSummary)
                logger.log(`report completed`, { reportClientId, sectionId })
            } catch (error) {
                throw logger.error(error, { reportClientId: report?.id })
            }
        })
    }

    private async onCancel() {
        const report = this.promiseManagers.flowReport.maybeGet()

        if (!report) {
            return
        }

        if (!report.parentClientId || !this.isSubtaskElementExecution(report)) {
            // cancels the entire task and takes you back to the todo list
            return this.props.onCancel()
        }

        // inside a subtask we want to cancel only the subtask and go back to the parent
        const answers = await this.props.fetchAnswers(report.parentClientId)

        const subTaskReportsAnswers = answers.filter((a) => a.type === 'reports' && a.value.includes(report.id))

        // we need to remove the subtask report from the parent report, we do that by creating a new answer
        // without the subtasks report id in it. This will ensure the subtask is not rendered
        await Promise.all(
            subTaskReportsAnswers.map(async (answer) => {
                const elementId = answer.element
                if (answer.type === 'reports') {
                    await this.createAnswer({
                        ...this.props.createAnswerBase(elementId, this.props.loopPathIds, answer),
                        type: 'reports',
                        loopPath: answer.loopPath,
                        report: report.parentClientId,
                        value: answer.value.filter((id) => id !== report.id),
                    })
                }
            })
        )
        await this.props.deleteSubtask(report.id)
        this.routeToParentReport(report)
    }

    private routeToParentReport(report: Report) {
        this.props.routeToExecutionPage(report.parentClientId, {
            comingFromActionScheduledInstance: this.props.comingFromActionScheduledInstance,
            comingFromScheduledInstance: this.props.comingFromScheduledInstance,
            comingFromSubTask: report.id,
            comingFromUnit: this.props.comingFromUnit,
            comingFromWorkflow: this.props.comingFromWorkflow,
        })
    }

    private removeAnswers = async (answersToRemove: Flows.Answer[]): Promise<void> => {
        const logger = this.props.deps.createLogger('ExecutionPage.removeAnswers')
        try {
            this.props.removeAnswers && (await this.props.removeAnswers(answersToRemove))
            const report = this.promiseManagers.flowReport.maybeGet()

            /* If a user has completed a subtask, then came back to it and removed
                an answer then we need to mark it as incomplete until they press the
                submit button on the subtask again */
            if (report?.parentClientId != null) {
                await this.props.markReportAsIncomplete(logger, report.id)
            }

            const flowAnswersState = promiseStateUpdate(this.state.flowAnswersState, (answers) => {
                const answerIdsToRemove = answersToRemove.map((a) => a.id)
                return answers.filter((a) => !answerIdsToRemove.includes(a.id))
            })

            this.setState({ flowAnswersState })
        } catch (error) {
            throw logger.error(error)
        }
    }

    private createAnswer = async (answer: Flows.Answer) => {
        const logger = this.props.deps.createLogger('ExecutionPage.createAnswer')
        try {
            await this.props.writeAnswer(answer)

            const report = this.promiseManagers.flowReport.maybeGet()
            const isFirstAnswer = answer.oldAnswer == null
            const newFlowAnswersState = promiseStateUpdate(this.state.flowAnswersState, (answers) => {
                answers = [...answers, answer]
                const oldAnswer = answers.find((a) => a.id === answer.oldAnswer)
                if (oldAnswer == null || this.startTimestamp >= oldAnswer.timestamp) return answers
                return answers.filter((a) => a.id !== oldAnswer.id)
            })

            // If a user has completed a subtask, then came back to it and changed
            // a answer then we need to mark it as incomplete until they press the
            // submit button on the subtask again
            if (report?.parentClientId != null) {
                await this.props.markReportAsIncomplete(logger, report.id)
            }

            this.setState({ flowAnswersState: newFlowAnswersState }, async () => {
                // Scroll to next element if this is the first time the element is answered
                // and the answer is not automatically generated using generateDefaultAnswer
                // and the element is one which triggers a scroll. Otherwise, scrolls to the
                // next header where available
                if (isFirstAnswer) {
                    const section = this.getSection(report)
                    const currenElement = findElement(section, answer.element)
                    if (!currenElement || !shouldScroll(currenElement)) return

                    // Find all elements and headers in the dom
                    const answerLoopPathIds = answer.loopPath ? fromLoopPath(answer.loopPath) : []
                    const answerLoopPath = toLoopPath(answerLoopPathIds)
                    const currentElementDomId = elementDomId(currenElement.id, answerLoopPath)
                    const nextDomElement = findNextDomElement(
                        '.AppCore__flow__ExecutionElement, .AppCore__flow__ExecutionPageBody__step-header, .AppCore__flow__ExecutionPageBody__loop-step-header',
                        currentElementDomId
                    )
                    if (!nextDomElement) return

                    // Only specifically when on a dom element, execute answer steps
                    if (nextDomElement.classList.contains('.AppCore__flow__ExecutionElement')) {
                        const nextIds = parseElementDomId(nextDomElement.id)

                        // Find the answers for the next element
                        const allFlowUnits = this.promiseManagers.allUnits.maybeGet()
                        const flowReport = this.promiseManagers.flowReport.mustGet()
                        const flowAnswers = this.promiseManagers.flowAnswers.mustGet()
                        const requiredGuidesMap = this.promiseManagers.requiredGuidesMap.mustGet()
                        const guideElementsContextMap = this.promiseManagers.guideElementsContextMap.mustGet()

                        const sectionSummary = this.getCurrentSectionSummary(
                            flowReport,
                            flowAnswers,
                            requiredGuidesMap,
                            guideElementsContextMap,
                            allFlowUnits
                        )
                        const scrollElementAnswer = findAnswer(
                            sectionSummary.answers,
                            nextIds.elementId,
                            fromLoopPath(nextIds.loopPath)
                        )

                        // We don't want to scroll to the element if the element has an answer and is not default generated
                        const isScrollElementAnswered =
                            scrollElementAnswer != null && !isDefaultGeneratedAnswer(scrollElementAnswer)
                        if (isScrollElementAnswered) return
                    }

                    // On some devices, if the on-screen keyboard is open when a selection
                    // is made, it can take a moment to retract, causing the scroll to fail,
                    // so lets give it some time
                    wait(100)
                    nextDomElement.scrollIntoView({ behavior: 'smooth' })
                }
            })
        } catch (error) {
            throw logger.error(error)
        }
    }

    private showSubmit = () => {
        if (this.props.alwaysShowSubmitButton) {
            return true
        }

        // Show the button disabled until the report and answers has been loaded
        const allFlowUnits = this.promiseManagers.allUnits.maybeGet()
        const report = this.promiseManagers.flowReport.maybeGet()
        const answers = this.promiseManagers.flowAnswers.maybeGet()
        const requiredGuidesMap = this.promiseManagers.requiredGuidesMap.maybeGet()
        const guideElementsContextMap = this.promiseManagers.guideElementsContextMap.maybeGet()

        // Do not ever show Submit for the 'guide' type
        if (report?.type === 'guide') return false

        if (report == null || answers == null || requiredGuidesMap == null || allFlowUnits == null) return true

        // Do not show if the summary has a completed date
        const sectionSummary = this.getCurrentSectionSummary(
            report,
            answers,
            requiredGuidesMap,
            guideElementsContextMap,
            allFlowUnits
        )

        return sectionSummary.completedDate == null || report.workflowRequiresSignature
    }

    private isSubmitDisabled = () => {
        // Disabled submit until the report and answers has been loaded
        const report = this.promiseManagers.flowReport.maybeGet()
        const answers = this.promiseManagers.flowAnswers.maybeGet()
        const uploads = this.promiseManagers.uploads.maybeGet()

        if (report == null || answers == null || uploads == null) return true

        // Disable submit if files are still uploading
        if (this.props.showFileProgress && uploads.some(hasUnsyncedData)) {
            return true
        }

        // If the user has already clicked the submit button, then disable submit
        return this.state.submitState.status === 'in-progress'
    }

    private showReportEditBanner = () => (
        <div className="AppCore__flow__ExecutionPageTopBar__report-edit-banner">
            <SVG_Info color="var(--white)" />
            <div className="AppCore__flow__ExecutionPageTopBar__report-edit-banner-text">
                {i18next.t('appcore:ExecutionPage.editingReport')}
            </div>
        </div>
    )

    /**
     * We want to hide the footer whenever the keyboard is up because it shows above the keyboard
     * Only relevant if the user has a touch keyboard (iOS or Android)
     */
    private onFocusChange = (event: FocusEvent) => {
        if (!this.props.hasTouchKeyboard) return

        // If it's a focus event we want the target
        // if it's a blur we want the "related target"
        const element = (event.type === 'focusin' ? event.target : event.relatedTarget) as HTMLElement

        // Is the target is an input element?
        const isInputFocus = element != null && (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA')

        if (!this.state.hideFooter) {
            // If the footer is currently visible, we want to hide if the focus is now on an input element
            if (!isInputFocus) return
            this.setState({ hideFooter: true })
        } else {
            // If the footer is currently hidden, we want to show if the focus is no longer an input element
            if (isInputFocus) return
            this.setState({ hideFooter: false })
        }
    }

    private saveUnitSubject = async (reportId: Flows.ReportId, flow: Flows.Flow, unit: KnownUnit) => {
        // Save it in the report on disk
        await this.props.saveUnitSubject(reportId, flow, unit)

        // Update it in the report in state
        await this.promiseManagers.flowReport.update((report) => ({
            ...report,
            subjectUnitId: unit.id,
            subjectUnitData: unit.data,
            subjectUnitVersion: unit.version,
        }))

        // Update the report subject state
        await this.promiseManagers.reportSubject.update(() => unit)
    }

    private shouldShowSummaryPage() {
        const report = this.promiseManagers.flowReport.maybeGet()
        if (report == null) return true

        return !readyToStart(report.flow.subjectType, { subjectUnitId: report.subjectUnitId })
    }

    private shouldShowOpenStateModal() {
        const report = this.promiseManagers.flowReport.maybeGet()
        if (report == null) return false

        // Can still edit a subtask if completed as long as the top level report is still open
        if (
            report.parentClientId != null &&
            report.progenitorOpenState === 'open' &&
            report.openState === 'completed'
        ) {
            return false
        }

        // Otherwise show the modal if not open
        return report.openState !== 'open'
    }

    private withFileUrlStates(
        report: Report,
        allAnswers: Flows.Answer[],
        allUnits: KnownUnit[],
        requiredGuidesMap: Map<Guide.Id, Pick<Guide.SyncGuide, 'isDeleted'>>,
        guideElementsContextMap: Map<Guide.Id, { guide: Guide.SyncGuide; workflow: WorkflowReference }>,
        uploads: Upload[],
        render: (fileUrlStates: FileUrlStates) => React.ReactElement<unknown>
    ) {
        // Find all the fileIds from answers that are relevant
        const flowSummary = this.getFlowSummary(
            report,
            allAnswers,
            requiredGuidesMap,
            guideElementsContextMap,
            allUnits
        )
        const relevantAnswers = flowSummary.answers
        const fileIds = getFileIdsFromAnswers(relevantAnswers)

        // Load them with file url manager
        return (
            <FileUrlManager
                deps={this.props.deps}
                fileIds={fileIds}
                uploads={uploads}
                readBlob={this.props.readBlob}
                render={render}
            />
        )
    }

    private fetchOriginatingReportsCount(report: Report): Promise<number> {
        if (!report.scheduledInstanceId) {
            return Promise.resolve(0)
        }

        return this.props.fetchOriginatingReportsCount(report.scheduledInstanceId)
    }

    private fetchOriginViewReportData(report: Report) {
        if (!report.scheduledInstanceId) {
            return null
        }

        return this.props.fetchOriginViewReportData(report.scheduledInstanceId)
    }

    private renderSummaryPage() {
        return (
            <ExecutionSummaryPage
                deps={this.props.deps}
                onBack={this.onBack}
                reportState={this.state.flowReportState}
                renderFooter={this.renderFooter}
                fetchUnitsByIds={this.props.fetchUnitsByIds}
                fetchUnitsByType={this.props.fetchUnitsByType}
                fetchUnitTypeName={this.props.fetchUnitTypeName}
                saveUnitSubject={this.saveUnitSubject}
                routeToProcedurePage={this.props.routeToProcedurePage}
                barcodeScanForUnits={this.props.barcodeScanForUnits?.scan}
                renderCachedImage={this.props.renderCachedImage}
            />
        )
    }

    private renderFooter = () => {
        return this.state.hideFooter ? null : this.props.footer
    }

    private renderExecutionPage() {
        return (
            <>
                <div className="AppCore__page__Page__wrapper">
                    <Page
                        deps={this.props.deps}
                        hideFooter={this.props.isIOS && this.state.hideFooter}
                        hideHeader={this.props.isIOS && this.state.hideHeader}
                        header={
                            this.props.showHeader && (
                                <ExecutionPageTopBar
                                    deps={this.props.deps}
                                    title={this.getTitle().toUpperCase()}
                                    onBack={this.onBack}
                                    rightButton={this.getHeaderOptions()}
                                >
                                    {this.promiseManagers.flowReport.render((report) => {
                                        return (
                                            <div>
                                                {report.completionDate && this.showReportEditBanner()}
                                                {report.parentReportTitle && (
                                                    <div
                                                        className={classNames(
                                                            'AppCore__flow__ExecutionPageTopBar__report-tile',
                                                            'parentReport'
                                                        )}
                                                        onClick={() => this.routeToParentReport(report)}
                                                    >
                                                        <ZapticReportTile
                                                            titleText={i18next.t('appcore:ExecutionPage.parentReport')}
                                                            detailText={report.parentReportTitle}
                                                            imageUrl={`${getPathPrefix()}shared/images/sub-task.svg`}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    })}
                                    {this.promiseManagers.reportSubject.render((reportSubject) => {
                                        if (!reportSubject) return null

                                        return this.promiseManagers.flowReport.render((report) => {
                                            return (
                                                <ReportSubject
                                                    className="AppCore__flow__ExecutionPageTopBar__report-tile"
                                                    deps={this.props.deps}
                                                    unitName={reportSubject.name}
                                                    imageUrl={reportSubject.data?.image}
                                                    withCachedImageUrl={this.props.withCachedImageUrl}
                                                    label={
                                                        report.type === 'action'
                                                            ? i18next.t('appcore:ExecutionPage.linkedUnit')
                                                            : null
                                                    }
                                                    onClick={() =>
                                                        this.props.routeForReportSubject &&
                                                        this.props.routeForReportSubject(reportSubject.id)
                                                    }
                                                />
                                            )
                                        })
                                    })}
                                    {this.props.headerExtra}
                                </ExecutionPageTopBar>
                            )
                        }
                        footer={this.renderFooter()}
                    >
                        {renderPromiseManagers(
                            tuple(
                                this.promiseManagers.flowReport,
                                this.promiseManagers.flowAnswers,
                                this.promiseManagers.allUnits,
                                this.promiseManagers.reportSubject,
                                this.promiseManagers.workflows,
                                this.promiseManagers.uploads,
                                this.promiseManagers.filesMetaData,
                                this.promiseManagers.userProfiles,
                                this.promiseManagers.requiredGuidesMap,
                                this.promiseManagers.guideElementsContextMap
                            ),
                            ([
                                report,
                                answers,
                                allUnits,
                                reportSubject,
                                workflows,
                                uploads,
                                filesMetaDataMap,
                                userProfiles,
                                requiredGuidesMap,
                                guideElementsContextMap,
                            ]) => {
                                return this.withFileUrlStates(
                                    report,
                                    answers,
                                    allUnits,
                                    requiredGuidesMap,
                                    guideElementsContextMap,
                                    uploads,
                                    (fileUrlStates) => {
                                        const flowSummary = this.getFlowSummary(
                                            report,
                                            answers,
                                            requiredGuidesMap,
                                            guideElementsContextMap,
                                            allUnits,
                                            report.type === 'guide' ? this.props.loopPathIds : []
                                        )

                                        const section = this.getSection(report)
                                        const steps = section.steps.filter((step) =>
                                            hasStepInSummary(flowSummary, step.id, this.props.loopPathIds)
                                        )

                                        const elementGuideIds = getAllElements(section, {
                                            traverseLoops: true,
                                        })
                                            .filter((element) => isGuideElement(element) && element.guideId)
                                            .map((element: Flows.GuideElement) => element.guideId)

                                        const elementTagIds: number[] = getGuideElementTagIdsFromSection(section)

                                        const reportEditDetails =
                                            report.completionDate && userProfiles.length
                                                ? {
                                                      reportStartDate: report.startDate,
                                                      reportCompletedDate: report.completionDate,
                                                      userProfiles,
                                                  }
                                                : null

                                        if (this.shouldShowOpenStateModal()) {
                                            const completedBy = userProfiles.find(
                                                (user) => user.userId === flowSummary.completedBy
                                            )

                                            return (
                                                <OpenStateModal
                                                    deps={this.props.deps}
                                                    openState={report.openState}
                                                    onBack={this.onOpenStateModalDismiss}
                                                    completedBy={completedBy}
                                                    reportType={report.type}
                                                />
                                            )
                                        }

                                        return (
                                            <>
                                                {this.props.submitFeedback && (
                                                    <ElementOptions
                                                        onFeedbackSubmission={this.submitFeedback.bind(this)}
                                                        onClose={this.closeElementOptions.bind(this)}
                                                        onFeedbackClick={() =>
                                                            this.openFeedback(this.state.elementSheet.element)
                                                        }
                                                        onCommentsClick={() =>
                                                            this.openComments(this.state.elementSheet.element)
                                                        }
                                                        onClearElement={this.clearCommentElement.bind(this)}
                                                        isQuoteClickable={({ workflowElement }) =>
                                                            hasElementInSummary(flowSummary, workflowElement)
                                                        }
                                                        onQuoteClick={({ workflowElement: elementId, loopPath }) => {
                                                            if (isNil(elementId) || isNil(loopPath)) {
                                                                return
                                                            }
                                                            this.closeElementOptions()
                                                            this.goToElement(report, elementId, loopPath)
                                                        }}
                                                        collaboratorUserIds={this.getCollaborators(report, true)}
                                                        commonDeps={this.props.deps}
                                                        featureFlags={this.props.featureFlags}
                                                        reportPostId={this.props.reportPostId}
                                                        sheet={this.state.elementSheet}
                                                        userProfiles={userProfiles}
                                                    />
                                                )}
                                                <ExecutionPageBody
                                                    deps={this.props.deps}
                                                    answers={answers}
                                                    reportEditDetails={reportEditDetails}
                                                    steps={steps}
                                                    flowSummary={flowSummary}
                                                    loopPathIds={this.props.loopPathIds}
                                                    guideId={this.props.guideId}
                                                    loopPlan={report.loopPlan}
                                                    scheduleParameters={report.scheduleParameters}
                                                    cachedUnitData={report.cachedUnitData}
                                                    allUnitsState={this.promiseManagers.allUnits.getState()}
                                                    flowUnitIds={report.flowUnitIds}
                                                    showValidation={this.state.showValidation}
                                                    createAnswerBase={this.props.createAnswerBase}
                                                    createAnswer={this.createAnswer}
                                                    routeToPostPage={this.props.routeToPostPage}
                                                    routeToReportPage={this.props.routeToReportPage}
                                                    routeToSection={(sectionId, loopPathIds) =>
                                                        this.props.routeToExecutionPage(report.id, {
                                                            sectionId,
                                                            loopPathIds,
                                                        })
                                                    }
                                                    removeAnswers={this.removeAnswers}
                                                    renderCachedImage={this.props.renderCachedImage}
                                                    onClickGuideParentInfo={this.props.onClickGuideParentInfo}
                                                    uploads={uploads}
                                                    selectFile={this.props.selectFile}
                                                    saveBlob={this.props.saveBlob}
                                                    saveFile={this.props.saveFile}
                                                    readBlob={this.props.readBlob}
                                                    cancelUpload={this.props.cancelUpload}
                                                    showFileProgress={this.props.showFileProgress}
                                                    savePathForRecovery={async (elementId) => {
                                                        if (this.props.savePathForRecovery == null) {
                                                            return
                                                        }

                                                        const scrollTop = this.getScrollPosition()
                                                        await this.props.savePathForRecovery(elementId, scrollTop)
                                                    }}
                                                    recovery={this.props.recovery}
                                                    workflows={workflows}
                                                    subtaskReports={report.subtaskReports}
                                                    createSubtask={this.props.createSubtask}
                                                    deleteSubtask={this.props.deleteSubtask}
                                                    routeToSubtask={(reportId) =>
                                                        this.props.routeToExecutionPage(reportId, {
                                                            comingFromActionScheduledInstance:
                                                                this.props.comingFromActionScheduledInstance,
                                                            comingFromScheduledInstance:
                                                                this.props.comingFromScheduledInstance,
                                                            comingFromUnit: this.props.comingFromUnit,
                                                            comingFromWorkflow: this.props.comingFromWorkflow,
                                                        })
                                                    }
                                                    routeToGuideExecutionPage={this.props.routeToGuideExecutionPage}
                                                    reportId={report.id}
                                                    originReportId={report.originId}
                                                    progenitorId={report.progenitorClientId ?? report.id}
                                                    subjectUnit={reportSubject}
                                                    featureFlags={this.props.featureFlags}
                                                    fetchReportHistory={() => this.props.fetchReportHistory(report)}
                                                    fetchUnitsByIds={this.props.fetchUnitsByIds}
                                                    fetchUnitType={this.props.fetchUnitType}
                                                    fetchUnitTypeName={this.props.fetchUnitTypeName}
                                                    fetchUnitsByType={this.props.fetchUnitsByType}
                                                    fetchOriginatingReportsCount={() =>
                                                        this.fetchOriginatingReportsCount(report)
                                                    }
                                                    fetchOriginViewReportData={() =>
                                                        this.fetchOriginViewReportData(report)
                                                    }
                                                    browserCameraDeviceIndexLookup={
                                                        this.props.browserCameraDeviceIndexLookup
                                                    }
                                                    cordovaGetMedia={this.props.cordovaGetMedia}
                                                    onShowPreviewChange={(modalOpen) => {
                                                        this.setState({
                                                            hideHeader: modalOpen,
                                                            hideFooter: modalOpen,
                                                        })
                                                    }}
                                                    barcodeScanForUnits={this.props.barcodeScanForUnits}
                                                    fileUrlStates={fileUrlStates}
                                                    withCachedImageUrl={this.props.withCachedImageUrl}
                                                    reportStartDate={report.startDate}
                                                    filesMetaDataMap={filesMetaDataMap}
                                                    promiseThunkQueue={this.promiseThunkQueue}
                                                    forceUseBrowserCameraAPI={this.props.forceUseBrowserCameraAPI}
                                                    elementToScrollToDetails={{
                                                        elementToScrollTo: this.state.elementToScrollTo,
                                                        clearElementScroll: () =>
                                                            this.setState({
                                                                elementToScrollTo: null,
                                                            }),
                                                    }}
                                                    featureEnabled={this.props.featureEnabled}
                                                    fetchElementGuides={() =>
                                                        this.props
                                                            .fetchSyncGuides(elementGuideIds, elementTagIds)
                                                            .then((syncGuides) =>
                                                                this.props.fetchAppGuides(syncGuides, true)
                                                            )
                                                    }
                                                    fetchFileGuideDetailsMap={this.props.fetchFileGuideDetailsMap}
                                                    fetchSyncGuides={this.props.fetchSyncGuides}
                                                    onLinkedGuidesToStep={this.props.onLinkedGuidesToStep}
                                                    onOpenFile={this.props.onOpenFile}
                                                    allowImageZoom={this.props.allowImageZoom}
                                                    isIOS={this.props.isIOS}
                                                    isAndroid={this.props.isAndroid}
                                                    footer={this.renderGuideExecutionFooter(
                                                        report,
                                                        this.props.guideElementId,
                                                        this.props.guideId
                                                    )}
                                                    getScrollPosition={this.getScrollPosition.bind(this)}
                                                    openOptions={
                                                        this.props.submitFeedback && this.openElementOptions.bind(this)
                                                    }
                                                    hasAccessToProcedure={this.props.hasAccessToProcedure}
                                                />
                                            </>
                                        )
                                    }
                                )
                            }
                        )}

                        {this.showSubmit() && (
                            <ExecutionPageSubmitButton
                                disabled={this.isSubmitDisabled()}
                                onSubmit={this.onSubmit}
                                label={this.getSubmitLabel()}
                            />
                        )}

                        {this.renderOptionsModal()}

                        {renderPromiseManagers(
                            tuple(
                                this.promiseManagers.reportSubject,
                                this.promiseManagers.userProfiles,
                                this.promiseManagers.flowReport
                            ),
                            ([reportSubject, userProfiles, report]) => {
                                // Transform the user profiles so we can look them up by userId
                                const profiles = keyBy(userProfiles || [], 'userId')

                                const collaborators = this.getCollaborators(report)

                                return this.renderExpertsModal(profiles, reportSubject, collaborators)
                            }
                        )}
                    </Page>
                </div>
                <PopupMessageModal
                    isOpen={this.state.isReauthModalOpen}
                    hasCustomOverlay={true}
                    heading={i18next.t('shared:Reauthentication.confirmation.confirmIdentity')}
                    message={i18next.t('shared:Reauthentication.confirmation.onReportSubmissionWithPopupWarning')}
                    submitLabel={i18next.t('shared:Reauthentication.confirmation.confirmIdentity')}
                    iconName="identity-check"
                    onCancel={() => this.setState({ isReauthModalOpen: false })}
                    onSubmit={() => {
                        this.setState({ isReauthModalOpen: false })
                        this.props.onSubmit(this.state.sectionSummary)
                    }}
                />
            </>
        )
    }

    private getCollaborators(report: Report, includeCurrentUser = false) {
        const collaborators = (report.collaborators?.accepted || []).concat(report.collaborators?.invited || [])
        if (includeCurrentUser) {
            return collaborators
        }

        // Exclude the current user from the list of collaborators
        return collaborators.filter((id) => id !== this.props.deps.user.id)
    }

    private hasCollaborators(report: Report) {
        return this.getCollaborators(report).length > 0
    }

    private getHeaderOptions(): JSX.Element {
        return (
            <div className="AppCore__flow__ExecutionPageTopBar__actions">
                {this.promiseManagers.flowReport.render((flowReport) => {
                    // No call an expert if this is not a task
                    if (flowReport.type !== 'task') {
                        return null
                    }

                    // No call an expert if the report was already completed
                    if (this.isCompletedReport(flowReport)) {
                        return null
                    }

                    // No call an expert button if there is no subject and no collaborators
                    const hasSubject = this.hasReportSubject(flowReport)
                    const hasCollaborators = this.hasCollaborators(flowReport)
                    if (!hasSubject && !hasCollaborators) {
                        return null
                    }

                    return (
                        <IconButton
                            border={false}
                            background={false}
                            iconName="help"
                            onClick={() => this.setState({ expertsModalOpen: true })}
                            size="medium"
                            className="AppCore__flow__ExecutionPageTopBar__HelpButton"
                            data-testid="execution-page-call-expert-button"
                            data-insight="execution-page-call-expert-button"
                        />
                    )
                })}
                {this.props.featureFlags.workflowFeedbackV2 && this.props.reportPostId && (
                    <IconButton
                        border={false}
                        background={false}
                        iconName={'comment'}
                        onClick={() => this.openComments(null)}
                        size="medium"
                        data-insight="execution-page-comments-button"
                        data-testid="execution-page-comments-button"
                    />
                )}
                <IconButton
                    border={false}
                    background={false}
                    iconName="ellipsis"
                    onClick={() => this.setState({ isOptionsModalOpen: true })}
                    size="medium"
                    data-testid="execution-page-options-button"
                    data-insight="execution-page-options-button"
                />
            </div>
        )
    }

    private getCancelOptionLabel = () => {
        const report = this.promiseManagers.flowReport.maybeGet()
        switch (report.type) {
            case 'action':
                return i18next.t('appcore:ExecutionPage.cancel.action')
            case 'guide':
                return i18next.t('appcore:ExecutionPage.cancel.guide')
            case 'training':
                return i18next.t('appcore:ExecutionPage.cancel.training')
            case 'task':
                return i18next.t('appcore:ExecutionPage.cancel.task')
            default:
                return i18next.t('appcore:common.cancel')
        }
    }

    private isSubSectionElementExecution = (report: Report) => {
        const parentSection = findSectionWithElement(report.flow, this.props.sectionId)
        // In Subsection element execution
        return parentSection != null
    }

    private isSubtaskElementExecution = (report: Report) => {
        const parentClientId = report.parentClientId
        // In Subtask element execution
        return parentClientId != null && report.type !== 'guide'
    }

    private isGuideElementExecution = (report: Report) => {
        const parentClientId = report.parentClientId
        // In Guide element execution
        return this.props.guideElementId && parentClientId != null && report.type === 'guide'
    }

    private getSubmitLabel = () => {
        const report = this.promiseManagers.flowReport.maybeGet()
        const defaultLabel = i18next.t('appcore:common.submit')

        if (report != null) {
            if (this.isSubSectionElementExecution(report)) {
                return i18next.t('appcore:ExecutionPage.submit.section')
            }

            if (this.isSubtaskElementExecution(report)) {
                return i18next.t('appcore:ExecutionPage.submit.subtask')
            }

            if (this.isGuideElementExecution(report)) {
                return i18next.t('appcore:ExecutionPage.submit.guide')
            }

            switch (report.type) {
                case 'action':
                    return i18next.t('appcore:ExecutionPage.submit.action')
                case 'guide':
                    return i18next.t('appcore:ExecutionPage.submit.guide')
                case 'training':
                    return i18next.t('appcore:ExecutionPage.submit.training')
                case 'task':
                    return i18next.t('appcore:ExecutionPage.submit.task')
                default:
                    return defaultLabel
            }
        }
        return defaultLabel
    }

    private renderOptionsModal() {
        const report = this.promiseManagers.flowReport.mustGet()

        const submitButton: BottomSheetOptionProps[] = this.showSubmit()
            ? [
                  {
                      optionLabel: this.getSubmitLabel(),
                      iconName: 'checkmark-grey',
                      onClick: () => {
                          this.setState({ isOptionsModalOpen: false })
                          this.onSubmit()
                      },
                  },
              ]
            : []

        const cancelButton: BottomSheetOptionProps[] = !this.isEditingReport(report)
            ? [
                  {
                      optionLabel: this.getCancelOptionLabel(),
                      labelStyles: { color: 'var(--red-500, #e3273d)' },
                      iconName: 'cross',
                      onClick: () => {
                          this.onCancel()
                      },
                  },
              ]
            : []

        return (
            <BaseBottomSheet
                isOpen={this.state.isOptionsModalOpen}
                onClose={() => this.setState({ isOptionsModalOpen: false })}
                title={i18next.t('appcore:ExecutionPage.optionsLabel')}
                size="small"
            >
                <BottomSheetOptions options={[...submitButton, ...cancelButton]} />
            </BaseBottomSheet>
        )
    }

    private renderExpertsModal(
        userProfiles: Record<number, Profile>,
        reportSubject: KnownUnit,
        collaborators: number[]
    ) {
        const onClose = () => this.setState({ expertsModalOpen: false, expertsModalSelectedExpert: null })

        // If a profile is already selected, show the specific profile
        const selectedUser = userProfiles[this.state.expertsModalSelectedExpert]
        if (selectedUser?.profileInfo) {
            return (
                <BaseBottomSheet
                    isOpen={this.state.expertsModalOpen}
                    onClose={onClose}
                    onBack={() => this.setState({ expertsModalSelectedExpert: null })}
                    title={selectedUser.name}
                    size="auto"
                >
                    {PhoneNumberSection(selectedUser.profileInfo, undefined, true, 'tel')}
                    {WhatsAppSection(selectedUser.profileInfo, undefined, true, true)}
                    {MsTeamsSection(selectedUser.profileInfo, undefined, true, true)}
                    {EmailSection(selectedUser, undefined, true)}
                    {UrlSection(selectedUser.profileInfo, undefined, true)}
                </BaseBottomSheet>
            )
        }

        // Otherwise, show the list of experts and collaborators
        const reportSubjectExperts = reportSubject?.experts ?? []
        const experts = reportSubjectExperts.map((expert) => userProfiles[expert]).filter(Boolean)
        const collaboratorsProfiles = collaborators.map((collaborator) => userProfiles[collaborator]).filter(Boolean)

        return (
            <BaseBottomSheet
                isOpen={this.state.expertsModalOpen}
                onClose={onClose}
                title={i18next.t('appcore:ExecutionPage.callAnExpert.label')}
                size="auto"
            >
                <ExpertsAndCollaboratorsList
                    experts={experts}
                    collaborators={collaboratorsProfiles}
                    onExpertClick={(expertId) => this.setState({ expertsModalSelectedExpert: expertId })}
                    reportSubject={reportSubject}
                />
            </BaseBottomSheet>
        )
    }

    private renderGuideExecutionFooter = (report: Report, guideElementId?: Flows.ElementId, guideId?: Guide.Id) =>
        this.props.guideElementId &&
        this.props.fetchWorkflowVersion && (
            <div>
                <MarkGuideAsCompleteFooter
                    createAnswerBase={this.props.createAnswerBase}
                    deps={this.props.deps}
                    fetchAnswers={this.props.fetchAnswers}
                    fetchParentReport={() =>
                        this.props
                            .fetchReport(report.id)
                            .then((guideReport) => this.props.fetchReport(guideReport.parentClientId))
                    }
                    fetchWorkflowVersion={this.props.fetchWorkflowVersion}
                    fetchSyncGuide={this.fetchSyncGuide}
                    guideElementId={guideElementId}
                    onSubmit={() =>
                        this.props.routeToExecutionPage(report.parentClientId, {
                            scrollToElementId: this.props.guideElementId,
                            scrollTop: this.props.parentScrollTop,
                        })
                    }
                    writeAnswer={this.props.writeAnswer}
                    loopPathIds={this.props.loopPathIds}
                    guideId={guideId}
                />
            </div>
        )

    private openElementOptions(element: QuotedElement) {
        if (this.props.featureFlags.workflowFeedbackV2) {
            this.setState({ elementSheet: { mode: 'options', element } })
        } else {
            this.setState({ elementSheet: { mode: 'feedback', element } })
        }
    }

    private closeElementOptions() {
        this.setState({ elementSheet: null })
    }

    private submitFeedback(message: string) {
        this.props.submitFeedback(this.state.elementSheet.element.id, message, this.state.elementSheet.element.loopPath)
    }

    private openComments(element: QuotedElement | null, focusedComment?: UUID) {
        this.setState({ elementSheet: { mode: 'comments', element, focusedComment } })
    }

    private openFeedback(element: QuotedElement) {
        this.setState({ elementSheet: { mode: 'feedback', element } })
    }

    private clearCommentElement() {
        this.setState({ elementSheet: { ...this.state.elementSheet, element: null } })
    }

    private fetchSyncGuide = async (guideId: Guide.Id) => {
        const guides = await this.props.fetchSyncGuides([guideId])
        return guides[0]
    }

    private tryRender = () => {
        return (
            <div className="AppCore__flow__ExecutionPage" ref={(ref) => (this.pageElement = ref)}>
                {this.shouldShowSummaryPage() ? this.renderSummaryPage() : this.renderExecutionPage()}
            </div>
        )
    }
}

function findNextDomElement(selector: string, domId: string) {
    const domElements = [...document.querySelectorAll(selector)]
    const index = domElements.findIndex((e) => e.id === domId)
    return domElements[index + 1]
}
